import React, { useEffect, useState, useMemo } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { Input } from "../../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../../shadcn/components/ui/button";
import { Label } from "../../../../../../shadcn/components/ui/label";
import { getBidderByUuid, updateBidderForEvent } from "services";
import CustomLoadingModal from "../IndividualBidders/CustomLoadingModal";
import { requestKYCValidation } from "services/bidder";

interface ApproverDetailsFormProps {
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  bidderUuid: any;
  init: any;
  bidder: any;
  outerNextStep?: string | null;
  outerVerificationStep?: string;
}
export default function JBApproverDetailsForm({
  bidderUuid,
  init,
  stepsTitles,
  activeStep,
  setActiveStep,
  bidder,
  outerNextStep = null,
  outerVerificationStep = "wait_for_consent",
}: ApproverDetailsFormProps) {
  const [loading, setLoading] = useState(false);
  //   Verified by Anita Sharma (Emp ID: LIC12345), Retail Assets
  // Email: anita.sharma@lichfl.com · Mobile: 9876500012
  const form = useBetaForm({
    approver_email: "",
    approver_mobile: "",
    approver_name: "",
    approver_employee_id: "",
    approver_department: "",
    approver_role: "",
  });
  const form_constraints = useMemo(() => {
    const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;
    const bidderEmail = bidder?.email?.trim().toLowerCase();
    const bidderMobile = bidder?.meta?.user_details?.mobile_number?.trim();

    return {
      approver_mobile: {
        presence: {
          allowEmpty: false,
        },
        format: {
          pattern: mobileNumberRegEx,
          message: "^Mobile number is not valid.",
        },
        length: {
          maximum: 10,
          message: "^Mobile number is too long (maximum is 10 characters)",
        },
        exclusion: bidderMobile
          ? {
              within: [bidderMobile],
              message:
                "^Approver mobile number cannot be the same as your mobile number",
            }
          : undefined,
      },
      approver_email: {
        presence: {
          allowEmpty: false,
        },
        email: true,
        exclusion: bidderEmail
          ? {
              within: [bidderEmail],
              message:
                "^Approver email cannot be the same as your email address",
            }
          : undefined,
      },
      approver_name: {
        presence: {
          allowEmpty: false,
        },
      },
      approver_employee_id: {
        presence: { allowEmpty: false },
      },
      approver_department: {
        presence: { allowEmpty: true },
      },
      approver_role: {
        presence: {
          allowEmpty: true,
        },
      },
    };
  }, [bidder]);
  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form, form_constraints, form?.value]);

  useEffect(() => {
    // Load existing approver details if available
    if (bidder?.kyc_data?.kyc_approver_details && form) {
      const approverDetails = bidder?.kyc_data?.kyc_approver_details;
      // Only set if values are different to avoid unnecessary updates
      if (
        form.getField("approver_email") !==
        (approverDetails.approver_email || "")
      ) {
        form.setField("approver_email", approverDetails.approver_email || "");
      }
      if (
        form.getField("approver_mobile") !==
        (approverDetails.approver_mobile || "")
      ) {
        form.setField("approver_mobile", approverDetails.approver_mobile || "");
      }
      if (
        form.getField("approver_name") !== (approverDetails.approver_name || "")
      ) {
        form.setField("approver_name", approverDetails.approver_name || "");
      }
      if (
        form.getField("approver_employee_id") !==
        (approverDetails.approver_employee_id || "")
      ) {
        form.setField(
          "approver_employee_id",
          approverDetails.approver_employee_id || "",
        );
      }
      if (
        form.getField("approver_department") !==
        (approverDetails.approver_department || "")
      ) {
        form.setField(
          "approver_department",
          approverDetails.approver_department || "",
        );
      }
      if (
        form.getField("approver_role") !== (approverDetails.approver_role || "")
      ) {
        form.setField("approver_role", approverDetails.approver_role || "");
      }
    }
  }, [bidder, form]);

  const handleSubmit = async () => {
    console.log("handleSubmit-----", form.validate(), form.value, form.errors);
    if (form.validate()) {
      setLoading(true);
      try {
        const data: any = await getBidderByUuid(bidderUuid);
        console.log("form?.value---", form?.value);
        await updateBidderForEvent({
          uuid: bidderUuid,
          meta: {
            ...data?.meta,
            submission_time: new Date().toISOString(),
            steps_status: {
              ...data?.meta?.steps_status,
              [outerNextStep === null ? "approver_details" : outerNextStep]:
                "completed",
            },
          },
          kyc_data: {
            ...data?.kyc_data,
            kyc_approver_details: {
              ...data?.kyc_data?.kyc_approver_details,
              ...form?.value,
              form_submission: "submitted",
            },
          },
          verification_step: outerVerificationStep
            ? outerVerificationStep
            : "wait_for_consent",
          // digio_request_status: "approved",
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
          const payload = {
            email: form.getField("approver_email"),
            event_uuid: data.event_uuid,
            bidder_uuid: bidderUuid,
          };
          if (data?.esigned_data?.consent_notified !== true)
            await requestKYCValidation(payload);
        } catch (error) {
          console.log("error: ", error);
        }

        setActiveStep(activeStep + 1);
        await init();
        setLoading(false);
      } catch (error) {
        console.error("Error submitting approver details:", error);
        setLoading(false);
      }
    }
  };

  return (
    <div className="py-2">
      {bidder?.kyc_data?.approver_details?.form_submission === "submitted" ? (
        <div className="text-sm">This step is completed.</div>
      ) : (
        <div>
          {/* Important Notice */}
          <div className="mb-4">
            <div className="border-[1px] border-[#E0C707] bg-[#FFFBF0] px-3 py-2 rounded-md">
              <div className="flex gap-2 md:gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-[#E0C707]"
                  >
                    <path
                      d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm1 15H9v-2h2v2zm0-4H9V5h2v6z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm md:text-base font-semibold text-[#B8860B] mb-1">
                    Important Notice
                  </div>
                  <div className="text-xs md:text-sm text-brand-textBlack leading-relaxed">
                    This step is required to mark your application as completed.
                    Otherwise, your KYC and bid application will be marked as
                    pending and you will not be able to participate in the
                    auction.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Support Information */}
          <div className="mb-4">
            <div className="border-[1px] border-[#4DBB3E] bg-[#FCFFFB] px-3 py-2 rounded-md">
              <div className="flex gap-2 md:gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="text-[#4DBB3E]"
                  >
                    <path
                      d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm-1 15H9v-2h2v2zm0-4H9V5h2v6z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="text-sm md:text-base font-semibold text-[#4DBB3E] mb-1">
                    Need Help?
                  </div>
                  <div className="text-xs md:text-sm text-brand-textBlack leading-relaxed">
                    If you did not receive approver details while submitting the
                    form at the bank, please connect with the authorized officer
                    or contact the Auction Bazaar support team for assistance.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-xs md:text-sm text-brand-textGrey mt-2 md:mt-4 mb-4">
            <strong className="text-brand-textBlack">Property:</strong>{" "}
            {bidder?.meta?.event_name}
          </div>
          <div className="font-semibold">Approver Information</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="approver_name">Approver Name*</Label>
              <Input
                placeholder="Enter Approver Name*"
                value={form.getField("approver_name")}
                onChange={(e: any) => {
                  form.setField("approver_name", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_name") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_name")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="approver_email">Approver Email*</Label>
              <Input
                placeholder="Enter Approver Email*"
                type="email"
                value={form.getField("approver_email")}
                onChange={(e: any) => {
                  form.setField("approver_email", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_email") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_email")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="approver_mobile">Approver Mobile*</Label>
              <Input
                type="number"
                placeholder="Enter Approver Mobile*"
                value={form.getField("approver_mobile")}
                onChange={(e: any) => {
                  if (e.target.value >= 0 || e.target.value == "") {
                    form.setField("approver_mobile", e?.target?.value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_mobile") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_mobile")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="approver_employee_id">Employee ID*</Label>
              <Input
                placeholder="Enter Employee ID*"
                value={form.getField("approver_employee_id")}
                onChange={(e: any) => {
                  form.setField("approver_employee_id", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_employee_id") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_employee_id")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="approver_department">Department</Label>
              <Input
                placeholder="Enter Department"
                value={form.getField("approver_department")}
                onChange={(e: any) => {
                  form.setField("approver_department", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_department") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_department")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="approver_role">Role</Label>
              <Input
                placeholder="Enter Role"
                value={form.getField("approver_role")}
                onChange={(e: any) => {
                  form.setField("approver_role", e?.target?.value);
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("approver_role") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("approver_role")?.[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-8 mb-16 md:mb-0 flex justify-center">
            <Button
              disabled={loading}
              className="w-full max-w-xl h-10 text-[18px]"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
      <CustomLoadingModal
        isOpen={loading}
        loadingText="Saving Details. Please wait"
      />
    </div>
  );
}
